import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useExportOptions } from '@/hooks/useExportOptions';

import type { useGetCollectionTableProps } from '../update/tabs/activities/activtiesConfig';
import type { AssessmentActivityRegisterFields } from '../update/tabs/activities/types';
import { buildOversightActivityRegisterToolbarConfig } from './registerToolbarConfig';

interface Props {
  title: string;
  /** Already-formatted count display string (e.g. `(12)`). */
  counter?: string;
  tableProps: ReturnType<typeof useGetCollectionTableProps>;
  getActiveRibbonFilters: () => FilterModal[];
  /**
   * Whether the current user may bulk-delete activities
   * (`delete:assessment_activity`, resolved on the page). When true, a "Delete"
   * action is surfaced in the toolbar's Danger zone.
   */
  hasDeletePermission: boolean;
  /** Disable the bulk-delete action (no rows selected). */
  isDeleteDisabled: boolean;
  /** Opens the bulk-delete confirmation modal (owned by the page). */
  onDelete: () => void;
}

/**
 * Object toolbar for the Oversight activities register, mounted only when the
 * `object_toolbar` flag is on. Owning `useExportOptions` here (rather than on the
 * page) keeps it off the legacy `ExportButton` path — with the flag off this
 * component never renders (CUS-164, mirroring the Indicators register CUS-150).
 *
 * The base config is export-only (see {@link buildOversightActivityRegisterToolbarConfig});
 * the selection-driven bulk-delete is added here as a Danger-zone action.
 */
export const OversightActivityRegisterToolbar: FC<Props> = ({
  title,
  counter,
  tableProps,
  getActiveRibbonFilters,
  hasDeletePermission,
  isDeleteDisabled,
  onDelete,
}) => {
  const { t } = useTranslation(['common']);
  const { t: at } = useTranslation(['common'], {
    keyPrefix: 'oversightAssessmentActivities',
  });
  const [showPdfModal, setShowPdfModal] = useState(false);
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } =
    useExportOptions<AssessmentActivityRegisterFields>({
      tableProps,
      entityLabel: title,
      getActiveRibbonFilters,
      onShowCustomPdfModal,
    });

  const toolbarConfig: ObjectToolbarConfig = useMemo(() => {
    const base = buildOversightActivityRegisterToolbarConfig({
      title,
      counter,
      exportOptions,
      editRibbon: ribbonConfig
        ? {
            groupLabel: t('edit'),
            label: t('objectToolbar.filterSettings'),
            onClick: ribbonConfig.openEditRibbon,
          }
        : undefined,
    });

    if (!hasDeletePermission) {
      return base;
    }

    return {
      ...base,
      actions: {
        ...base.actions,
        danger: [
          {
            id: 'delete',
            label: at('delete_activity_button'),
            iconName: 'trash-01',
            onClick: onDelete,
            disabled: isDeleteDisabled,
          },
        ],
      },
    };
  }, [
    title,
    counter,
    exportOptions,
    ribbonConfig,
    hasDeletePermission,
    isDeleteDisabled,
    onDelete,
    t,
    at,
  ]);

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={true}
      />
    </>
  );
};
